import { Database } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { CMSService } from "@/services/cmsService";
import { useEditorStore } from "@/store/editorStore";

interface CollectionSourceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSourceSelected: (sourceId: string) => void;
}

export const CollectionSourceDialog: React.FC<CollectionSourceDialogProps> = ({
  isOpen,
  onClose,
  onSourceSelected,
}) => {
  const { toast } = useToast();
  const { project } = useEditorStore();

  const [datasets, setDatasets] = useState<Array<Record<string, unknown>>>([]);
  const [selectedDataset, setSelectedDataset] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const loadDatasets = useCallback(async () => {
    if (!project?.id) return;

    try {
      setIsLoading(true);
      const availableDatasets = await CMSService.loadProjectCMSData(project.id);
      setDatasets(availableDatasets);
    } catch (error) {
      console.error("Error loading datasets:", error);
      toast({
        variant: "destructive",
        title: "Failed to load collections",
        description: "Could not load available CMS collections.",
      });
    } finally {
      setIsLoading(false);
    }
  }, [project?.id, toast]);

  useEffect(() => {
    if (isOpen) {
      void loadDatasets();
    }
  }, [isOpen, loadDatasets]);

  const handleConfirm = () => {
    if (selectedDataset) {
      onSourceSelected(selectedDataset);
      onClose();
    }
  };

  const handleCancel = () => {
    setSelectedDataset("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="bg-gray-800 border-gray-700 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Database className="h-5 w-5 text-blue-400" />
            Select Collection Source
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Choose a collection from your CMS to use as the data source for this list component.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          <div>
            <Label className="text-gray-300 text-sm">Collection</Label>
            <p className="text-gray-400 text-xs mb-2">
              Choose which collection to use as the data source for this list
            </p>
            <Select value={selectedDataset} onValueChange={setSelectedDataset} disabled={isLoading}>
              <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                <SelectValue
                  placeholder={isLoading ? "Loading collections..." : "Select a collection..."}
                />
              </SelectTrigger>
              <SelectContent className="bg-gray-700 border-gray-600">
                {datasets
                  .filter((dataset) => dataset?.id && dataset.id.trim() !== "")
                  .map((dataset) => (
                    <SelectItem key={dataset.id} value={dataset.id} className="text-white group">
                      <div className="flex items-center gap-2">
                        <Database className="h-3 w-3" />
                        <span>{dataset.name}</span>
                        <Badge
                          variant="outline"
                          className="text-xs text-gray-400 border-white group-hover:text-black group-hover:border-black transition-colors"
                        >
                          {dataset.rowCount} items
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {datasets.length === 0 && !isLoading && (
            <div className="text-center py-4">
              <Database className="h-8 w-8 text-gray-500 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">No collections found</p>
              <p className="text-gray-500 text-xs">Create collections in the CMS tab first</p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-gray-700">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedDataset || isLoading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Create Collection
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
